import React from 'react';
import classNames from 'classnames';

import Locale from 'data/locale';
import Tooltip from 'ui/tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import TooltipBuilder from 'tooltips/builder';
import Data from 'data';
import { useContentCreator } from 'components/cclink';

const _L = Locale('ui-calculator');

const foiNames = {
  frames: {name: `Frames`},
  total: {name: `Total`},
  length: {name: `Len`, tooltip: 'Animation Length'},
  af: {name: `AF`, tooltip: 'Action Frame'},
  af1: {name: `AF1`, tooltip: 'Action Frame 1'},
  af2: {name: `AF2`, tooltip: 'Action Frame 2'},
  af3: {name: `AF3`, tooltip: 'Action Frame 3'},
  r1: {name: `R1`, tooltip: 'Right Swing 1'},
  r2: {name: `R2`, tooltip: 'Right Swing 2'},
  l1: {name: `L1`, tooltip: 'Left Swing'},
  average: {name: `Avg`, tooltip: 'Average Frames per Hit'},
  delay: {name: 'Delay', tooltip: 'Skill cooldown'},
};

function foiHeader(name) {
  if (!name) return null;
  const fn = foiNames[name];
  if (fn) {
    if (!fn.tooltip) return <th key={name}>{_L(fn.name)}</th>;
    return (
      <Tooltip tooltip={_L(fn.tooltip)} key={name}>
        <th>{_L(fn.name)}</th>
      </Tooltip>
    );
  }
  const m = name.match(/^hit(\d+(-\d+)?)$/);
  return <th key={name}>{m ? (m[2] ? _L`Hits ${m[1]}` : _L`Hit ${m[1]}`) : name}</th>;
}

function WsmTooltip({ animation, wsm }) {
  const builder = new TooltipBuilder(_L`Weapon Speed Modifier`, wsm);
  if (!wsm) builder.line(_L`Unarmed`);
  const items = animation.weaponList(wsm).map(id => Locale.strings[Data.weapons[id]?.namestr]).filter(Boolean).sort();
  for (const name of items) {
    builder.line(name);
  }
  return builder.build();
}

const typeNames = {
  ias: {name: 'IAS', hit: 'hit', hits: 'hits', tooltip: 'Increased Attack Speed', extended: true},
  oias: {name: 'oIAS', hit: 'hit', hits: 'hits', tooltip: 'Off-weapon Increased Attack Speed'},
  wias: {name: 'wIAS', hit: 'hit', hits: 'hits', tooltip: 'Weapon Increased Attack Speed', extended: true},
  wias2: {name: 'wIAS', hit: 'hit', hits: 'hits', tooltip: 'Weapon Increased Attack Speed'},
  fcr: {name: 'FCR', hit: 'cast', hits: 'casts', tooltip: 'Faster Cast Rate'},
};

function AnimationTable({ animation }) {
  const [ccNoWsm, setCcNoWsm] = React.useState(false);
  const [ccNoFrames, setCcNoFrames] = React.useState(false);
  const isCC = useContentCreator();
  const table = React.useMemo(() => animation.fullTable(), [animation]);
  if (!table) return null;
  let [cols, data] = table;

  function rowProp(r, i, v) {
    const props = {className: "d2p-frames"};
    let span = 1;
    const accel = animation.accel;
    while (r + span < data.length && data[r + span].frames[i] === v) {
      const {minaccel, maxaccel} = data[r + span];
      if (accel >= minaccel && accel <= maxaccel) {
        props.className += " d2p-current";
      }
      span += 1;
    }
    if (span > 1) props.rowSpan = span;
    return props;
  }

  const extended = typeNames[animation.type]?.extended;
  let wsm = (extended && animation.wsmlist) || [0];
  let wsmCol = wsm.length > 1 ? wsm.indexOf(animation.wsm || 0) : -1;
  const baseaccel = 100 - (animation.mode === 'SQ' ? 30 : 0); // animation.baseaccel + (animation.wsm || 0);// - (animation.weapon?.weaponspeed || 0);
  const extraRowOpt = extended && wsm.length > 1 && !animation.noinventory;
  const extraRow = extraRowOpt && !ccNoWsm;
  const typeTip = _L(typeNames[animation.type]?.tooltip);
  if (extended && wsm.length > 1 && ccNoWsm) {
    wsm = wsm.map(w => w === animation.wsm ? w : null);
    wsmCol = -1;
  }
  if (cols.length > 1 && ccNoFrames) {
    cols = cols.map((v, i) => i === cols.length - 1 ? 'frames' : null);
  }

  return (
    <div className="d2p-breakpoints">
      {!!isCC && (
        <div>
          {cols.length > 1 && <label><input type="checkbox" checked={ccNoFrames} onChange={e => setCcNoFrames(!!e.target.checked)}/> {_L`Hide AF`}</label>}
          {!!extraRowOpt && <label><input type="checkbox" checked={ccNoWsm} onChange={e => setCcNoWsm(!!e.target.checked)}/> {_L`Hide WSM`}</label>}
        </div>
      )}
      <table>
        <thead>
          {extraRow && (
            <tr>
              <th colSpan={cols.filter(Boolean).length}>{_L(animation.label || `Frames`)}</th>
              <Tooltip tooltip={_L`Effective IAS`}>
                <th rowSpan={2} className="d2p-accel">{_L`EIAS`}</th>
              </Tooltip>
              <Tooltip tooltip={typeTip}>
                <th colSpan={wsm.length}>{_L(typeNames[animation.type].name)}</th>
              </Tooltip>
            </tr>
          )}
          <tr>
            {cols.map(foiHeader)}
            {extended && !extraRow && (
              <Tooltip tooltip={_L`Effective IAS`}>
                <th className="d2p-accel">{_L`EIAS`}</th>
              </Tooltip>
            )}
            {extraRow && wsm.map((w, i) => w != null && (
              <Tooltip key={`wsm${i}`} tooltip={<WsmTooltip animation={animation} wsm={w}/>}>
                <th className={classNames({"d2p-current": i === wsmCol})}>{w}</th>
              </Tooltip>
            ))}
            {!extraRow && !animation.noinventory && (
              <Tooltip tooltip={typeTip}>
                <th>{_L(typeNames[animation.type]?.name || animation.type)}</th>
              </Tooltip>
            )}
          </tr>
        </thead>
        <tbody>
          {data.map(({frames, minaccel, maxaccel, params}, r) => (
            <tr key={minaccel} className={classNames({"d2p-current": animation.accel >= minaccel && animation.accel <= maxaccel})}>
              {frames.map((value, i) => !(r > 0 && data[r - 1].frames[i] === value) && cols[i] != null && <td key={cols[i]} {...rowProp(r, i, value)}>{value}</td>)}
              {extended && <td className="d2p-accel">{minaccel - baseaccel}</td>}
              {!animation.noinventory && params.map((value, i) => wsm[i] != null && <td key={`wsm${i}`} className={classNames({"d2p-disabled": value < 0, "d2p-current": i === wsmCol})}>{value > 0 ? value : null}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SkillAnimation({ animation }) {
  const [expanded, setExpanded] = React.useState(false);

  if (!Array.isArray(animation)) animation = [animation];
  const expandable = !!(animation.length > 1 || (animation[0].type && animation[0].minaccel < animation[0].maxaccel));

  React.useEffect(() => {
    if (!expandable) setExpanded(false);
  }, [expandable]);

  const type = typeNames[animation[0].type] || typeNames.ias;
  const combine = animation.every(anim => anim.type === animation[0].type);
  const hits = combine ? animation.reduce((sum, anim) => sum + anim.hits, 0) : animation[0].hits;
  const fpa = combine ? animation.reduce((sum, anim) => sum + anim.fpa, 0) / animation.length : animation[0].fpa;
  const persecond = parseFloat((25 * hits / fpa).toFixed(2));

  return (
    <div className={classNames("d2p-skill-damage", {"d2p-expanded": expanded, "d2p-expandable": expandable})}>
      <span className="d2p-damage-header" onClick={() => expandable && setExpanded(e => !e)}>
        {animation[0].type === 'fcr' ? _L`Cast Rate` : _L`Attack Rate`}: {combine ? animation.map(anim => anim.fpa).join(' / ') : fpa} {_L`frames`}{hits > 1 && ' ' + _L`per ${hits} ${_L(type.hits)}`}
        {` (${_L`${persecond} ${persecond === 1 ? _L(type.hit) : _L(type.hits)} per second`})`}
        {!!expandable && ' '}
        {!!expandable && <FontAwesomeIcon icon={expanded ? faCaretUp : faCaretDown}/>}
      </span>
      {expanded && (
        <div className="d2p-animation-list">
          {animation.map((anim, index) => <AnimationTable key={index} animation={anim}/>)}
        </div>
      )}
    </div>
  );
}
